import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { motion, useInView } from 'framer-motion';
import React, { useRef } from 'react';
import { Art } from '../types';

interface MasonryGalleryProps {
    artList: Art[];
    onArtClick: (id: number) => void;
}

interface GalleryItemProps {
    art: Art;
    onArtClick: (id: number) => void;
    index: number;
}

const GalleryItem: React.FC<GalleryItemProps> = ({ art, onArtClick, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-50px' });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: (index % 2) * 0.08 }}
        >
            <Box
                data-testid="gallery-frame"
                onClick={() => onArtClick(art.id)}
                style={{ outline: '1px solid #c9a84c', outlineOffset: '8px' }}
                sx={{ cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        '& img': {
                            transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94)',
                        },
                        '&:hover img': { transform: 'scale(1.06)' },
                        '& .art-overlay': { opacity: 0, transition: 'opacity 0.4s' },
                        '&:hover .art-overlay': { opacity: 1 },
                    }}
                >
                    <img
                        src={art.image}
                        alt={art.title}
                        style={{ width: '100%', display: 'block' }}
                        loading="lazy"
                    />
                    <Box
                        className="art-overlay"
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                            p: 2,
                            pt: 5,
                        }}
                    >
                        <Typography
                            data-testid="overlay-title"
                            style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)' }}
                            sx={{
                                color: '#f0ede8',
                                fontFamily: '"Cormorant Garamond", serif',
                                letterSpacing: '0.04em',
                            }}
                        >
                            {art.title}
                        </Typography>
                        {art.medium && (
                            <Typography
                                variant="caption"
                                sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif', display: 'block' }}
                            >
                                {art.medium}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </motion.div>
    );
};

const MasonryGallery: React.FC<MasonryGalleryProps> = ({ artList, onArtClick }) => {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 80vw)', sm: 'repeat(2, minmax(0, 33vw))' },
                justifyContent: 'space-evenly',
                alignItems: 'center',
                rowGap: '120px',
            }}
        >
            {artList.map((art, index) => (
                <GalleryItem key={art.id} art={art} onArtClick={onArtClick} index={index} />
            ))}
        </Box>
    );
};

export default MasonryGallery;
